import React, { useMemo, useState } from 'react'
import { useQueries } from '@tanstack/react-query'
import { useLocation, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { Box, Button as MuiButton, Menu, MenuItem, Typography } from '@mui/material'
import { ChevronDownIcon } from '@heroicons/react/24/outline'

import { QUERY_KEYS } from '../../apiLayer/queryKeys'
import { processHandlers } from '../../apiLayer/processHandlers'
import { useSetActiveOrganization } from '../../apiLayer/queryHooks/mutations'
import useAppState from '../../hooks/useAppState'
import { useTranslation } from '../../shared/hooks/useIntractionTranslate'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY } from '../../constants/colors'

import { getVisibleOrgMemberships } from './getVisibleOrgMemberships'
import { isSoleMembershipPlatformDefault } from './shouldHideOrgChrome'
import { buildOrgLabelByOrgId } from './organizationSwitcherHelpers'
import { useOrgTenant } from './OrgContext'
import { useOrgSwitch, useOrgSwitchHandlers } from './orgSwitch'
import { getSafeRouteAfterOrgSwitch } from './orgSwitch/orgSwitchNavigation'

/**
 * Workspace switcher for org-scoped shell: visible memberships + optional super-admin create org.
 */
export const OrganizationSwitcher: React.FC = () => {
  const { t } = useTranslation()
  const { state } = useAppState()
  const { activeMembership } = useOrgTenant()
  const { isSwitching } = useOrgSwitch()
  const orgSwitchHandlers = useOrgSwitchHandlers()
  const navigate = useNavigate()
  const location = useLocation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const visible = useMemo(() => getVisibleOrgMemberships(state.user), [state.user])
  const currentOrgId = activeMembership?.organizationId

  const labelQueries = useQueries({
    queries: visible.map((m) => ({
      queryKey: QUERY_KEYS.ORGANIZATION_DETAIL(m.organizationId),
      queryFn: () => processHandlers.getOrganization(state, { organizationId: m.organizationId }),
      enabled: visible.length > 0 && Boolean(state.user?.id),
      staleTime: 5 * 60 * 1000
    }))
  })

  const labelByOrgId = useMemo(() => buildOrgLabelByOrgId(visible, labelQueries), [labelQueries, visible])

  // Placeholder while an org name loads — never expose the raw organization id.
  const labelPlaceholder = t('org.switcher.workspace')

  const setActiveOrgMutation = useSetActiveOrganization({
    orgSwitch: orgSwitchHandlers,
    onSuccess: () => {
      const safeRoute = getSafeRouteAfterOrgSwitch(location.pathname)
      if (safeRoute) {
        navigate(safeRoute, { replace: true })
      }
    },
    onError: () => {
      toast.error(t('org.switcher.switchFailed'))
    }
  })

  const isSwitchBusy = isSwitching || setActiveOrgMutation.isPending

  const showMenuTrigger = visible.length > 1

  if (visible.length === 0) {
    return null
  }

  // Hide chrome entirely when the user's sole membership is the platform
  // default org — surfacing "Intraction" as the user's org name in that case
  // is misleading, since they're effectively unaffiliated.
  if (isSoleMembershipPlatformDefault(state.user)) {
    return null
  }

  if (visible.length === 1) {
    return (
      <Box sx={{ px: { xs: 1.5, sm: 2, md: 2 }, pt: { xs: 1, sm: 1.5, md: 1.5 }, pb: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: INTERAX_TYPOGRAPHY.fieldText2.fontFamily,
            fontSize: INTERAX_TYPOGRAPHY.fieldText2.fontSize,
            color: INTERAX_COLORS.thunderGrey,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {labelByOrgId[visible[0].organizationId] ?? labelPlaceholder}
        </Typography>
      </Box>
    )
  }

  const currentLabel =
    (currentOrgId && labelByOrgId[currentOrgId]) ||
    (visible[0] && labelByOrgId[visible[0].organizationId]) ||
    t('org.switcher.workspace')

  return (
    <Box sx={{ px: { xs: 1.5, sm: 2, md: 2 }, pt: { xs: 1, sm: 1.5, md: 1.5 }, pb: 0 }}>
      {showMenuTrigger ? (
        <>
          <MuiButton
            variant="outlined"
            size="small"
            disabled={isSwitchBusy}
            aria-disabled={isSwitchBusy}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="true"
            aria-label={t('org.switcher.openMenuAria')}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              width: '100%',
              justifyContent: 'space-between',
              textTransform: 'none',
              color: INTERAX_COLORS.white,
              borderColor: INTERAX_COLORS.white10,
              py: { xs: 0.75, sm: 1, md: 1 },
              px: { xs: 1, sm: 1.25, md: 1.25 }
            }}
            endIcon={<ChevronDownIcon style={{ width: 18, height: 18 }} aria-hidden />}
          >
            <Typography
              component="span"
              variant="body2"
              sx={{
                fontFamily: INTERAX_TYPOGRAPHY.fieldText2.fontFamily,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textAlign: 'left',
                flex: 1,
                minWidth: 0
              }}
            >
              {currentLabel}
            </Typography>
          </MuiButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: anchorEl?.offsetWidth,
                  bgcolor: INTERAX_COLORS.offBlack,
                  border: `1px solid ${INTERAX_COLORS.white10}`
                }
              }
            }}
          >
            {visible.map((m) => (
              <MenuItem
                key={m.organizationId}
                selected={m.organizationId === currentOrgId}
                disabled={isSwitchBusy}
                onClick={() => {
                  setAnchorEl(null)
                  if (m.organizationId !== currentOrgId) {
                    const label = labelByOrgId[m.organizationId] ?? labelPlaceholder
                    setActiveOrgMutation.mutate({
                      organizationId: m.organizationId,
                      targetLabel: label
                    })
                  }
                }}
                sx={{ color: INTERAX_COLORS.white }}
              >
                {labelByOrgId[m.organizationId] ?? labelPlaceholder}
              </MenuItem>
            ))}
          </Menu>
        </>
      ) : (
        <Typography
          variant="body2"
          sx={{
            fontFamily: INTERAX_TYPOGRAPHY.fieldText2.fontFamily,
            fontSize: INTERAX_TYPOGRAPHY.fieldText2.fontSize,
            color: INTERAX_COLORS.thunderGrey,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {currentLabel}
        </Typography>
      )}
    </Box>
  )
}
